import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { getAvatarUrl, getImageUrl } from "src/Common/common";
import ConfirmDialog from "src/Common/confirmdialog";
import TimeOffService from "src/Services/API/timeoff.service";
import { TYPE_META, formatDate, dayCount } from "./TimeOff";
import ConflictedWos from "./ConflictedWos";
import AvailabilityPreview from "./AvailabilityPreview";

// Right-side drawer that shows time-off request details.
//
// The parent owns the request to display (`item`) and clears it on `onClose`.
// This component owns the open/close animation: it mounts when `item` is
// non-null, slides itself in on the next frame, and on close triggers the
// slide-out and then calls `onClose` after the transition finishes so the
// parent can null out `item` and unmount us.
function TimeOffDetail({ item, onClose, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // local copy of the item so the drawer keeps rendering content while it
  // slides out, even after the parent has cleared its state. cleared when
  // the close transition ends so the drawer fully unmounts.
  const [displayed, setDisplayed] = useState(null);
  const [conflictingWos, setConflictingWos] = useState([]);
  const [rejectionReason, setRejectionReason] = useState("");
  // confirmation modal: null = closed; { kind: "approve" | "reject", status? }
  const [confirm, setConfirm] = useState(null);
  const drawerRef = useRef(null);
  // armed when a new item arrives; consumed once the layout effect performs the
  // one-time slide-in. Keeps that effect from re-opening the drawer when the
  // user closes it (close sets open=false while item is briefly still set).
  const pendingOpen = useRef(false);

  useEffect(() => {
    if (item) {
      setDisplayed(item);
      setRejectionReason("");
      // ensure we start from the closed position; the layout effect below
      // flips to open after the browser has committed this closed state.
      pendingOpen.current = true;
      setOpen(false);
      return;
    }
    setOpen(false);
  }, [item]);

  // After the drawer mounts in its closed position (translate-x-full), force a
  // layout read so the browser commits that starting position, then flip to
  // open. Reading offsetWidth synchronously flushes the closed transform, so
  // the subsequent open is a real style change the transition can animate —
  // unlike a double rAF, this can't be coalesced into a single paint on mount.
  useLayoutEffect(() => {
    if (!pendingOpen.current || !displayed) return;
    pendingOpen.current = false;
    // eslint-disable-next-line no-unused-expressions
    drawerRef.current?.offsetWidth; // force reflow at the closed position
    setOpen(true);
  }, [displayed]);

  // status: 1 = approved with PTO, 2 = approved without PTO.
  // Defer opening the dialog so the originating click finishes bubbling —
  // otherwise OutsideClickHandler can treat that click as "outside" the
  // just-mounted dialog and close it immediately.
  const requestApprove = (status) => {
    setTimeout(() => setConfirm({ kind: "approve", status }), 0);
  };
  const requestReject = () => setTimeout(() => setConfirm({ kind: "reject" }), 0);

  const doConfirm = async () => {
    if (!displayed?.id || submitting || !confirm) return;
    try {
      setSubmitting(true);
      if (confirm.kind === "approve") {
        await TimeOffService.updateStatus(displayed.id, confirm.status);
        onApprove?.(displayed, confirm.status);
      } else {
        // status 3 = rejected
        await TimeOffService.updateStatus(displayed.id, 3, rejectionReason);
        onReject?.(displayed, rejectionReason);
      }
      setConfirm(null);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  // load conflicting work orders whenever a new request is shown
  useEffect(() => {
    if (!item?.id) return;
    let cancelled = false;
    TimeOffService.getConflictingWos(item.id)
      .then((res) => {
        if (!cancelled && (res.status === 200 || res.status === 201)) {
          setConflictingWos(res.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) setConflictingWos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [item?.id]);

  // set of "YYYY-MM-DD" days that have an assigned job, for the calendar dots.
  // computed before the early return so the hook order stays stable.
  const assignedJobDates = useMemo(() => {
    const set = new Set();
    for (const wo of conflictingWos) {
      if (!wo.startDate) continue;
      const d = new Date(wo.startDate);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      set.add(`${y}-${m}-${day}`);
    }
    return set;
  }, [conflictingWos]);

  const handleTransitionEnd = (e) => {
    // only react to our own transform transition, not children's transitions
    if (e.target !== e.currentTarget) return;
    if (!open) {
      setDisplayed(null);
      onClose?.();
    }
  };

  // fully unmounted between sessions
  if (!displayed) return null;

  const type = TYPE_META[displayed.type] || TYPE_META[3];
  const days = dayCount(displayed.startDate, displayed.endDate);

  return (
    <>
      {/* backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 z-40 transition-opacity duration-300 ease-out ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => setOpen(false)}
      />
      {/* drawer */}
      <div
        ref={drawerRef}
        onTransitionEnd={handleTransitionEnd}
        className={`fixed top-0 right-0 h-full w-[384px] flex flex-col items-start gap-px border-l border-[#E5E7EB] bg-white shadow-[0_20px_25px_-5px_rgba(0,0,0,0.10),0_8px_10px_-6px_rgba(0,0,0,0.10)] z-50 overflow-y-auto transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="w-full flex justify-between items-start p-5 border-b border-[#F3F4F6] bg-[#F9FAFB]/30">
          <div className="flex items-center gap-3">
            <img
              src={getAvatarUrl(displayed.user?.avatar)}
              className="w-10 h-10 rounded-full border border-[#E2E8F0] object-cover"
              alt=""
            />
            <div className="flex flex-col">
              <span className="text-[18px] font-InterBold text-[#1E293B] leading-[22.5px]">
                {displayed.user?.name || displayed.user?.email || "-"}
              </span>
              <span
                className="text-[14px] font-InterRegular text-[#6B7280] leading-5"
                style={{ letterSpacing: "0.055px" }}
              >
                {displayed.user?.roles?.[0]?.name || "Technician"}
              </span>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="text-[#9CA3AF] hover:text-[#1E293B] text-[20px] leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Type + Submitted (two columns under the header) */}
        <div className="w-full flex items-start px-[24px] pt-5 pb-2">
          <div className="flex-1 flex flex-col gap-2">
            <span className="self-stretch text-[11px] font-InterSemiBold text-[#6B7280] uppercase leading-[16.5px]">
              Type
            </span>
            <span
              className="flex items-center gap-2 text-[14px] font-InterMedium text-[#1E293B] leading-5"
              style={{ letterSpacing: "0.014px" }}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${type.dotClass}`} />
              {type.label}
            </span>
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <span className="self-stretch text-[11px] font-InterSemiBold text-[#6B7280] uppercase leading-[16.5px]">
              Submitted
            </span>
            <span
              className="text-[14px] font-InterMedium text-[#1E293B] leading-5"
              style={{ letterSpacing: "0.014px" }}
            >
              {displayed.createdAt
                ? new Date(displayed.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                  })
                : "-"}
            </span>
          </div>
        </div>

        <div className="w-full flex flex-col gap-5 px-6 py-2">
          {/* Dates Requested */}
          <div className="flex flex-col gap-2">
            <span className="self-stretch text-[11px] font-InterSemiBold text-[#6B7280] uppercase leading-[16.5px]">
              Dates Requested
            </span>
            <div className="self-stretch flex items-center gap-1 p-2 rounded border border-[#F3F4F6] bg-[#F9FAFB]">
              <img src={getImageUrl("/images/common/calendar.svg")} className="w-[13px] h-[14px]" alt="" />
              <span className="text-[14px] font-InterMedium text-[#1E293B] leading-5">
                {formatDate(displayed.startDate)}
                {displayed.endDate &&
                  displayed.endDate !== displayed.startDate &&
                  ` - ${formatDate(displayed.endDate)}`}
              </span>
              <span
                className="text-[14px] font-InterRegular text-[#6B7280] leading-5"
                style={{ letterSpacing: "0.068px" }}
              >
                ({days} {days === 1 ? "day" : "days"})
              </span>
            </div>
          </div>

          {/* Note from Technician */}
          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-InterSemiBold text-[#6B7280] uppercase leading-[16.5px]">
              Note from Technician
            </span>
            <div className="self-stretch flex flex-col items-start p-3 rounded-md border border-[#F3F4F6] bg-[#F9FAFB]">
              <span className="text-[14px] font-InterRegular italic text-[#374151] leading-5 whitespace-pre-wrap">
                {displayed.notes ? `"${displayed.notes}"` : "—"}
              </span>
            </div>
          </div>

          {/* Scheduled Work During This Time */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <img src={getImageUrl("/images/timeoff/wo.svg")} className="w-[12px] h-[12px]" alt="" />
              <span className="text-[11px] font-InterSemiBold text-[#6B7280] uppercase leading-[16.5px]">
                Scheduled Work During This Time
              </span>
            </div>

            <ConflictedWos wos={conflictingWos} />
          </div>

          {/* Availability Preview */}
          <AvailabilityPreview
            startDate={displayed.startDate}
            endDate={displayed.endDate}
            assignedJobDates={assignedJobDates}
          />

          {displayed.status === 0 && (
            <>
              {/* Rejection Reason */}
              <div className="flex flex-col gap-2">
                <span className="text-[11px] font-InterSemiBold text-[#6B7280] uppercase leading-[16.5px]">
                  Rejection Reason (Optional)
                </span>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="If rejecting, please provide a reason..."
                  className="self-stretch flex justify-center items-start pt-2 pr-2 pb-7 pl-2 rounded-md border border-[#E5E7EB] bg-[#F9FAFB] text-[14px] font-InterRegular text-[#9CA3AF] leading-5 placeholder:text-[#9CA3AF] resize-none focus:outline-none focus:border-[#D1D5DB]"
                />
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-3 pt-2 border-t border-[#F3F4F6]">
                <button
                  type="button"
                  onClick={() => requestApprove(1)}
                  disabled={submitting}
                  className="w-full h-12 flex items-center justify-center gap-2 rounded-[8px] bg-[#0065FD] hover:bg-[#0058DB] text-white disabled:opacity-60"
                >
                  <span className="text-[16px] leading-6">✓</span>
                  <span
                    className="text-center text-[16px] font-OpenSansSemiBold font-[600] text-white leading-6 capitalize"
                    style={{ letterSpacing: "-0.016px" }}
                  >
                    Approve With PTO
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => requestApprove(2)}
                  disabled={submitting}
                  className="w-full h-12 flex items-center justify-center gap-2 rounded-[8px] bg-[#0065FD] hover:bg-[#0058DB] text-white disabled:opacity-60"
                >
                  <span className="text-[16px] leading-6">✓</span>
                  <span
                    className="text-center text-[16px] font-OpenSansSemiBold font-[600] text-white leading-6 capitalize"
                    style={{ letterSpacing: "-0.016px" }}
                  >
                    Approve Without PTO
                  </span>
                </button>
                <button
                  type="button"
                  onClick={requestReject}
                  disabled={submitting}
                  className="self-stretch h-12 flex items-center justify-center gap-2 rounded-[12px] border border-[#FCA5A5] bg-white hover:bg-[#FEF2F2] text-[#DC2626] disabled:opacity-60"
                >
                  <span className="text-[16px] leading-5">✕</span>
                  <span className="text-[16px] font-InterSemiBold leading-5">Reject Request</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        shown={!!confirm}
        title={
          confirm?.kind === "approve"
            ? confirm.status === 1
              ? "Approve with PTO?"
              : "Approve without PTO?"
            : "Reject this request?"
        }
        description={
          confirm?.kind === "approve"
            ? `This will approve ${displayed.user?.name || "the technician"}'s time-off request${
                confirm.status === 1 ? " and deduct from PTO." : " without using PTO."
              }`
            : `This will reject ${displayed.user?.name || "the technician"}'s time-off request.`
        }
        onConfirmClicked={doConfirm}
        onCancelClicked={() => !submitting && setConfirm(null)}
      />
    </>
  );
}

export default TimeOffDetail;
